import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { EMPTY_AVAILABILITY_PARAMETERS, EMPTY_CONTRACT_PARAMETERS, EMPTY_TRANSFER_FORM_LIST_CONFIGURATION, EMPTY_TRANSFER_FORM_LIST_CRITERIA } from "../constant/transferConstant";
import { Transfer } from "../objects/transfer";
import { TransferListCriteria } from "../objects/transferListCriteria";
import { TransferState } from "../objects/transferState";
import { setTransferPreference } from "../utils/setTransferPreference";
import { Column } from "../objects/column";
import { Contract } from "../../customProvider/objects/contract";
import { CancellationRule } from "../objects/cancellationRule";
import { transformTransfertBackendData } from "../utils/transformBackendData/transformBackendData";
import { TransferContractParameters } from "../objects/transferContractParameters";
import { TransferPrice } from "../objects/transferPrice";
import { TransferAvailabilityParameters } from "../objects/transferAvailability";
import { Cms } from "../../cms/objects/cms";


const initialState: TransferState = {
    transfer_list: [],
    transfer_list_refresh: false,
    transfer_list_configuration: EMPTY_TRANSFER_FORM_LIST_CONFIGURATION,
    transfer_list_loading: false,
    transfer_list_count: 0,
    transfer_list_criteria: EMPTY_TRANSFER_FORM_LIST_CRITERIA,
    first_preference_loaded: false,

    transfer: null,
    selected_contract: null,
    transfer_duplicate: null,


    transfer_cancellation_rules: [],
    transfer_cancellation_rules_loading: true,

    // price
    transfer_contract_parameter: EMPTY_CONTRACT_PARAMETERS,
    transfer_availability_parameter: EMPTY_AVAILABILITY_PARAMETERS,
    vat: 0,
    transfer_prices: [],
    catalog_price: { id: -1, catalog_price: false },
    margin_master_markup: false,

    allowed_cms: []
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addTransferListConfigurationPreferenceReducer = (state: TransferState, action: PayloadAction<Preferences[]>): TransferState => ({
    ...state, transfer_list_configuration: { ...state.transfer_list_configuration, preferences: [...state.transfer_list_configuration.preferences, ...action.payload] }
});

const setTransferListConfigurationPreferenceReducer = (state: TransferState, action: PayloadAction<Preferences[]>): TransferState => ({
    ...state, transfer_list_configuration: { ...state.transfer_list_configuration, preferences: action.payload }
});
const refreshTransferListReducer = (state: TransferState): TransferState => ({
    ...state, transfer_list_refresh: !state.transfer_list_refresh
});

const setPreferenceReducer = (state: TransferState, action: PayloadAction<Preferences[]>): TransferState => {
    const { preferences, columns, order_by, order } = setTransferPreference(action.payload, state.transfer_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        transfer_list_configuration: { ...state.transfer_list_configuration, preferences, columns },
        transfer_list_criteria: { ...state.transfer_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: TransferState, action: PayloadAction<number>): TransferState => {
    const columns = cloneDeep([...state.transfer_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, transfer_list_configuration: { ...state.transfer_list_configuration, columns } };
};

const setColumnsReducer = (state: TransferState, action: PayloadAction<Column[]>): TransferState => ({
    ...state, transfer_list_configuration: { ...state.transfer_list_configuration, columns: action.payload }
});

const updateTransferListReducer = (state: TransferState, action: PayloadAction<Transfer[]>): TransferState => ({
    ...state, transfer_list: action.payload
});
const updateTransferListCriteriaReducer = (state: TransferState, action: PayloadAction<TransferListCriteria>): TransferState => ({
    ...state, transfer_list_criteria: action.payload
});
const updateTransferReducer = (state: TransferState, action: PayloadAction<Transfer | null>): TransferState => ({
    ...state, transfer: transformTransfertBackendData(action.payload)
});
const updateTransferListLoadingReducer = (state: TransferState, action: PayloadAction<boolean>): TransferState => ({
    ...state, transfer_list_loading: action.payload
});
const updateTransferListCountReducer = (state: TransferState, action: PayloadAction<number>): TransferState => ({
    ...state, transfer_list_count: action.payload
});
const updateSelectedContractReducer = (state: TransferState, action: PayloadAction<null | Contract>): TransferState => ({
    ...state, selected_contract: action.payload
});
// ------------------ transfer duplication ---------------------- //
const updateTransferDuplicateReducer = (state: TransferState, action: PayloadAction<Transfer | null>): TransferState => ({
    ...state, transfer_duplicate: action.payload
});
// ------------------ cancellation rules ---------------------- //
const addTransferCancellationRuleReducer = (state: TransferState, action: PayloadAction<CancellationRule>): TransferState => ({
    ...state, transfer_cancellation_rules: [{ ...action.payload, periods: action.payload?.periods?.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) || [] }, ...state.transfer_cancellation_rules]
});
const updateTransferCancellationRuleReducer = (state: TransferState, action: PayloadAction<CancellationRule>): TransferState => ({
    ...state, transfer_cancellation_rules: state.transfer_cancellation_rules.map((cancellation_rule) => cancellation_rule.id === action.payload.id ? { ...action.payload, periods: action.payload.periods.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) } : cancellation_rule)
});
const updateTransferCancellationRulesReducer = (state: TransferState, action: PayloadAction<CancellationRule[]>): TransferState => ({
    ...state, transfer_cancellation_rules: action.payload.map((rule) => ({ ...rule, periods: rule?.periods?.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) || [] }))
});
const updateTransferCancellationRulesLoadingReducer = (state: TransferState, action: PayloadAction<boolean>): TransferState => ({
    ...state, transfer_cancellation_rules_loading: action.payload
});
const deleteTransferCancellationRuleReducer = (state: TransferState, action: PayloadAction<number>): TransferState => ({
    ...state, transfer_cancellation_rules: state.transfer_cancellation_rules.filter((cancellation_rule) => cancellation_rule.id !== action.payload)
});

// ------------------ price ---------------------- //
const setTransferContractParametersReducer = (state: TransferState, action: PayloadAction<TransferContractParameters>): TransferState => ({
    ...state, transfer_contract_parameter: action.payload
});
const setTransferAvailabilityParametersReducer = (state: TransferState, action: PayloadAction<TransferAvailabilityParameters>): TransferState => ({
    ...state, transfer_availability_parameter: { ...action.payload, transfer_availability_duration: action.payload.transfer_availability_duration?.map((duration) => ({ ...duration, start_date: duration.start_date ? new Date(duration.start_date) : null, end_date: duration.end_date ? new Date(duration.end_date) : null })) }
});
const updateVatReducer = (state: TransferState, action: PayloadAction<number>): TransferState => ({
    ...state, vat: action.payload
});

const updateMarginMasterMarkupReducer = (state: TransferState, action: PayloadAction<boolean>): TransferState => ({
    ...state, margin_master_markup: action.payload
});

const setTransferPricesReducer = (state: TransferState, action: PayloadAction<TransferPrice[]>): TransferState => ({
    ...state, transfer_prices: action.payload
});

const setCatalogPriceReducer = (state: TransferState, action: PayloadAction<{ id: number, catalog_price: boolean }>): TransferState => ({
    ...state, catalog_price: action.payload
});

const updateAllowedCmsReducer = (state: TransferState, action: PayloadAction<Cms[]>): TransferState => ({
    ...state, allowed_cms: action.payload
});

const resizeColumnReducer = (state: TransferState, action: PayloadAction<ColumnResizePayload>): void => {
    state.transfer_list_configuration.columns = applyColumnResize(state.transfer_list_configuration.columns, action.payload);
};

export const transfer = createSlice({
    name: 'transfer',
    initialState,
    reducers: {
        addTransferListConfigurationPreference: addTransferListConfigurationPreferenceReducer,
        setTransferListConfigurationPreference: setTransferListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        setColumns: setColumnsReducer,
        updateTransferList: updateTransferListReducer,
        refreshTransferList: refreshTransferListReducer,
        updateTransferListCriteria: updateTransferListCriteriaReducer,
        updateTransfer: updateTransferReducer,
        updateTransferListLoading: updateTransferListLoadingReducer,
        updateTransferListCount: updateTransferListCountReducer,
        updateSelectedContract: updateSelectedContractReducer,
        updateTransferDuplicate: updateTransferDuplicateReducer,
        addTransferCancellationRule: addTransferCancellationRuleReducer,
        updateTransferCancellationRule: updateTransferCancellationRuleReducer,
        updateTransferCancellationRules: updateTransferCancellationRulesReducer,
        updateTransferCancellationRulesLoading: updateTransferCancellationRulesLoadingReducer,
        deleteTransferCancellationRule: deleteTransferCancellationRuleReducer,
        setTransferContractParameters: setTransferContractParametersReducer,
        setTransferAvailabilityParameters: setTransferAvailabilityParametersReducer,
        updateVat: updateVatReducer,
        updateMarginMasterMarkup: updateMarginMasterMarkupReducer,
        setTransferPrices: setTransferPricesReducer,
        setCatalogPrice: setCatalogPriceReducer,
        updateAllowedCms: updateAllowedCmsReducer,
        resizeColumn: resizeColumnReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addTransferListConfigurationPreference,
    setTransferListConfigurationPreference,
    toggleColumn,
    setColumns,
    updateTransferList,
    refreshTransferList,
    updateTransferListCriteria,
    updateTransfer,
    updateTransferListLoading,
    updateTransferListCount,
    updateSelectedContract,
    updateTransferDuplicate,
    addTransferCancellationRule,
    updateTransferCancellationRule,
    updateTransferCancellationRules,
    updateTransferCancellationRulesLoading,
    deleteTransferCancellationRule,
    setTransferContractParameters,
    setTransferAvailabilityParameters,
    updateVat,
    updateMarginMasterMarkup,
    setTransferPrices,
    setCatalogPrice,
    updateAllowedCms,
    resizeColumn
} = transfer.actions;